import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import {
  BuildingLibraryIcon,
  BuildingOffice2Icon,
  BanknotesIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  InformationCircleIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { teamApi } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import BranchesPanel from './branches/BranchesPanel';

interface InstitutionSetupWizardProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
}

const REGULATORS = [
  { value: 'FDIC', label: 'FDIC', desc: 'State-chartered, non-Fed member banks', icon: BuildingLibraryIcon, color: 'bg-blue-100 text-blue-600' },
  { value: 'OCC', label: 'OCC', desc: 'Nationally chartered banks', icon: BuildingOffice2Icon, color: 'bg-indigo-100 text-indigo-600' },
  { value: 'FED', label: 'Federal Reserve', desc: 'State-chartered Fed member banks', icon: BanknotesIcon, color: 'bg-green-100 text-green-600' },
  { value: 'NCUA', label: 'NCUA', desc: 'Credit unions', icon: ShieldCheckIcon, color: 'bg-amber-100 text-amber-600' },
] as const;

const CHARTER_OPTIONS: Record<string, { value: string; label: string }[]> = {
  FDIC: [{ value: 'STATE_NONMEMBER', label: 'State Non-Member' }],
  OCC: [{ value: 'NATIONAL', label: 'National Bank' }, { value: 'THRIFT', label: 'Federal Thrift' }],
  FED: [{ value: 'STATE_MEMBER', label: 'State Member' }],
  NCUA: [{ value: 'FEDERAL_CU', label: 'Federal Credit Union' }, { value: 'STATE_CU', label: 'State Credit Union' }],
};

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

// CRA-aligned asset size buckets per annually-adjusted CRA asset threshold (effective 2026-01-07).
// Values are semantic so threshold-only adjustments in future years don't require re-keying stored data.
const ASSET_BUCKETS = [
  { value: 'SMALL', label: '<$412 million' },
  { value: 'INTERMEDIATE', label: '$412 million to <$1.649 billion' },
  { value: 'LARGE', label: '$1.649 billion +' },
];

interface FdicLookupResult {
  cert: number;
  found: boolean;
  active: boolean;
  institutionName: string | null;
  city: string | null;
  state: string | null;
  primaryRegulator: 'FDIC' | 'OCC' | 'FED' | 'NCUA' | null;
  charterType: 'STATE_MEMBER' | 'STATE_NONMEMBER' | 'NATIONAL' | 'FEDERAL_CU' | 'STATE_CU' | 'THRIFT' | null;
}

export default function InstitutionSetupWizard({ isOpen, onClose, teamId }: InstitutionSetupWizardProps) {
  const [step, setStep] = useState(0);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const userInstitutionType = useAuthStore(s => s.user?.institutionType);
  const isCreditUnion = userInstitutionType === 'CREDIT_UNION';

  // Step 0 fields
  const [institutionName, setInstitutionName] = useState('');
  const [primaryRegulator, setPrimaryRegulator] = useState('');
  const [charterType, setCharterType] = useState('');
  const [state, setState] = useState('');

  // Step 1 fields
  const [fdicCertNumber, setFdicCertNumber] = useState('');
  const [institutionLei, setInstitutionLei] = useState('');
  const [msaCodes, setMsaCodes] = useState('');
  const [assetSizeBucket, setAssetSizeBucket] = useState('');
  const [fdicPeerAssetMin, setFdicPeerAssetMin] = useState('');
  const [fdicPeerAssetMax, setFdicPeerAssetMax] = useState('');
  const [lastExamDate, setLastExamDate] = useState('');

  // FDIC auto-detect state
  const [fdicLookup, setFdicLookup] = useState<FdicLookupResult | null>(null);
  const [fdicLookupLoading, setFdicLookupLoading] = useState(false);
  const [fdicLookupError, setFdicLookupError] = useState<string | null>(null);
  const [autoAppliedCert, setAutoAppliedCert] = useState<number | null>(null);

  // Last CRA exam date auto-detect state (Wallace, May 2026)
  const [examDateSuggestion, setExamDateSuggestion] = useState<{
    examDate: string | null;
    confidence: number;
    reasoning: string;
    source: string;
    modelUsed: string | null;
  } | null>(null);
  const [examDateLookupLoading, setExamDateLookupLoading] = useState(false);
  const [examDateLookupError, setExamDateLookupError] = useState<string | null>(null);

  // Fetch existing data for pre-population (re-run scenario)
  const { data: existing } = useQuery({
    queryKey: ['institutionSetup', teamId],
    queryFn: () => teamApi.getInstitutionSetup(teamId).then(r => r.data),
    enabled: isOpen && !!teamId,
  });

  // Pre-populate when existing data loads
  useEffect(() => {
    if (!existing) return;
    if (existing.institutionName) setInstitutionName(existing.institutionName);
    if (existing.primaryRegulator) setPrimaryRegulator(existing.primaryRegulator);
    if (existing.charterType) setCharterType(existing.charterType);
    if (existing.state) setState(existing.state);
    if (existing.fdicCertNumber) setFdicCertNumber(String(existing.fdicCertNumber));
    if (existing.institutionLei) setInstitutionLei(existing.institutionLei);
    if (existing.msaCodes?.length) setMsaCodes(existing.msaCodes.join(', '));
    if (existing.assetSizeBucket) setAssetSizeBucket(existing.assetSizeBucket);
    if (existing.fdicPeerAssetMin) setFdicPeerAssetMin(String(existing.fdicPeerAssetMin));
    if (existing.fdicPeerAssetMax) setFdicPeerAssetMax(String(existing.fdicPeerAssetMax));
    if (existing.lastExamDate) setLastExamDate(existing.lastExamDate);
  }, [existing]);

  // Auto-select charter type when regulator changes and there's only one option
  useEffect(() => {
    if (primaryRegulator) {
      const options = CHARTER_OPTIONS[primaryRegulator] || [];
      if (options.length === 1) {
        setCharterType(options[0].value);
      } else {
        // Reset if the current charter doesn't belong to the new regulator
        if (!options.find(o => o.value === charterType)) {
          setCharterType('');
        }
      }
    }
  }, [primaryRegulator, charterType]);

  // Force NCUA for credit unions — the FDIC database doesn't include CUs, so
  // there's nothing to auto-detect from a cert. We pre-select NCUA on first
  // open if institutionType=CREDIT_UNION and let the user override only by
  // changing institutionType in their user profile.
  useEffect(() => {
    if (isCreditUnion && !primaryRegulator) {
      setPrimaryRegulator('NCUA');
    }
  }, [isCreditUnion, primaryRegulator]);

  // FDIC cert → primary regulator auto-detect.
  // When the user enters or changes the FDIC cert, debounce 500ms then call
  // /api/institution-lookup. On success, auto-apply the regulator + charter
  // ONLY if the user hasn't already manually selected (or for the same cert
  // we've already auto-applied). This keeps the bank from being silently
  // overridden if they explicitly chose something different.
  useEffect(() => {
    // Credit unions: skip — FDIC database doesn't contain CUs.
    if (isCreditUnion) return;

    const trimmed = fdicCertNumber.trim();
    if (!trimmed) {
      setFdicLookup(null);
      setFdicLookupError(null);
      return;
    }

    const cert = parseInt(trimmed, 10);
    if (!Number.isFinite(cert) || cert <= 0) return;

    const handle = setTimeout(async () => {
      setFdicLookupLoading(true);
      setFdicLookupError(null);
      try {
        const res = await teamApi.lookupByFdicCert(cert);
        const result = res.data as FdicLookupResult;
        setFdicLookup(result);

        // Auto-apply on first successful match for this cert. We track
        // `autoAppliedCert` so re-typing the same cert doesn't re-stomp on
        // user edits, but a new cert triggers a fresh apply.
        if (result.found && autoAppliedCert !== cert) {
          if (result.primaryRegulator) setPrimaryRegulator(result.primaryRegulator);
          if (result.charterType) setCharterType(result.charterType);
          if (result.institutionName && !institutionName) {
            setInstitutionName(result.institutionName);
          }
          if (result.state && !state) setState(result.state);
          setAutoAppliedCert(cert);
        }
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 502) {
          setFdicLookupError('FDIC API is unreachable — please verify the regulator manually.');
        } else {
          setFdicLookupError('Lookup failed. Please verify the regulator manually.');
        }
        setFdicLookup(null);
      } finally {
        setFdicLookupLoading(false);
      }
    }, 500);

    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fdicCertNumber, isCreditUnion]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const msaArray = msaCodes
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

      return teamApi.saveInstitutionSetup(teamId, {
        institutionName,
        primaryRegulator,
        charterType,
        state,
        fdicCertNumber: fdicCertNumber ? parseInt(fdicCertNumber, 10) : null,
        institutionLei: institutionLei || null,
        msaCodes: msaArray,
        assetSizeBucket: assetSizeBucket || null,
        fdicPeerAssetMin: fdicPeerAssetMin ? parseInt(fdicPeerAssetMin, 10) : null,
        fdicPeerAssetMax: fdicPeerAssetMax ? parseInt(fdicPeerAssetMax, 10) : null,
        lastExamDate: lastExamDate || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
      queryClient.invalidateQueries({ queryKey: ['institutionSetup'] });
      queryClient.invalidateQueries({ queryKey: ['peerBenchmarkConfig'] });
      setStep(2);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to save institution setup');
    },
  });

  const canContinueStep0 = institutionName.trim() && primaryRegulator && charterType && state;

  // Auto-detect last CRA exam date — calls the LLM-assisted lookup and
  // stores the result as a SUGGESTION the analyst confirms before applying.
  const handleAutoDetectExamDate = async () => {
    if (!institutionName.trim()) {
      setExamDateLookupError('Enter the institution name first.');
      return;
    }
    setExamDateLookupLoading(true);
    setExamDateLookupError(null);
    setExamDateSuggestion(null);
    try {
      const res = await teamApi.autoDetectExamDate(teamId, {
        institutionName: institutionName.trim(),
        fdicCertNumber: fdicCertNumber ? parseInt(fdicCertNumber, 10) : null,
        state: state || null,
        primaryRegulator: (primaryRegulator || null) as any,
      });
      setExamDateSuggestion(res.data);
    } catch (err: any) {
      setExamDateLookupError(err?.response?.data?.error || 'Auto-detect failed — enter the date manually.');
    } finally {
      setExamDateLookupLoading(false);
    }
  };

  const handleApplyExamDateSuggestion = () => {
    if (examDateSuggestion?.examDate) {
      setLastExamDate(examDateSuggestion.examDate);
    }
  };

  const handleSaveAndContinue = () => {
    saveMutation.mutate();
  };

  const handleFinish = (destination?: string) => {
    onClose();
    if (destination) navigate(destination);
  };

  // Reset step when closing/reopening
  useEffect(() => {
    if (!isOpen) setStep(0);
  }, [isOpen]);

  const charterOptions = primaryRegulator ? (CHARTER_OPTIONS[primaryRegulator] || []) : [];

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {/* Progress dots */}
                <div className="flex justify-center mb-6">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`w-2.5 h-2.5 rounded-full mx-1 transition-colors ${
                        i === step ? 'bg-ocean-600' : i < step ? 'bg-ocean-300' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>

                {/* Step 0: Institution Profile */}
                {step === 0 && (
                  <>
                    <Dialog.Title as="h3" className="text-2xl font-bold text-navy-900 mb-2">
                      Institution Profile
                    </Dialog.Title>
                    <Dialog.Description className="text-gray-600 mb-6">
                      Tell us about your institution. This configures compliance checks, peer benchmarking, and regulatory intelligence across the platform.
                    </Dialog.Description>

                    {/* Institution Name */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Institution Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={institutionName}
                        onChange={(e) => setInstitutionName(e.target.value)}
                        placeholder="e.g. First Community Bank"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500"
                      />
                    </div>

                    {/* Primary Regulator */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Primary Regulator <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {REGULATORS.map((reg) => (
                          <button
                            key={reg.value}
                            onClick={() => setPrimaryRegulator(reg.value)}
                            className={`flex items-center p-3 border-2 rounded-lg text-left transition-all ${
                              primaryRegulator === reg.value
                                ? 'border-ocean-500 bg-ocean-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${reg.color}`}>
                              <reg.icon className="w-5 h-5" />
                            </div>
                            <div className="ml-3">
                              <div className="font-semibold text-sm text-gray-900">{reg.label}</div>
                              <div className="text-xs text-gray-500">{reg.desc}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Charter Type */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Charter Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={charterType}
                          onChange={(e) => setCharterType(e.target.value)}
                          disabled={!primaryRegulator}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 disabled:bg-gray-100"
                        >
                          <option value="">{primaryRegulator ? 'Select charter type' : 'Select regulator first'}</option>
                          {charterOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* State */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          State <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={state}
                          onChange={(e) => setState(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500"
                        >
                          <option value="">Select state</option>
                          {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={() => setStep(1)}
                        disabled={!canContinueStep0}
                        className="flex-1 bg-ocean-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-ocean-700 focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                      >
                        Continue
                        <ArrowRightIcon className="w-4 h-4 ml-2" />
                      </button>
                      <button
                        onClick={() => handleFinish()}
                        className="px-6 py-3 rounded-lg font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Skip
                      </button>
                    </div>
                  </>
                )}

                {/* Step 1: Regulatory IDs & Peer Configuration */}
                {step === 1 && (
                  <>
                    <Dialog.Title as="h3" className="text-2xl font-bold text-navy-900 mb-2">
                      Regulatory IDs & Peer Group
                    </Dialog.Title>
                    <Dialog.Description className="text-gray-600 mb-4">
                      These fields power Peer Benchmarking, Financial Metrics, and HMDA analysis. All optional — you can add them later in Settings.
                    </Dialog.Description>

                    <div className="space-y-4 max-h-[400px] overflow-y-auto pr-1">
                      {/* FDIC Cert */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          FDIC Certificate Number
                        </label>
                        <input
                          type="number"
                          value={fdicCertNumber}
                          onChange={(e) => setFdicCertNumber(e.target.value)}
                          placeholder="e.g. 12345"
                          disabled={isCreditUnion}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 disabled:bg-gray-100 disabled:text-gray-500"
                        />
                        <p className="text-xs text-gray-500 mt-1 flex items-center">
                          <InformationCircleIcon className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                          {isCreditUnion
                            ? 'Credit unions are NCUA-supervised — no FDIC cert needed.'
                            : 'Find yours at research.fdic.gov. We\'ll auto-detect your prudential regulator.'}
                        </p>

                        {/* FDIC lookup feedback */}
                        {fdicLookupLoading && (
                          <p className="text-xs text-ocean-600 mt-2 flex items-center">
                            <span className="inline-block w-3 h-3 border-2 border-ocean-600 border-t-transparent rounded-full animate-spin mr-2" />
                            Looking up institution at FDIC…
                          </p>
                        )}

                        {fdicLookup?.found && !fdicLookupLoading && (
                          <div className="mt-2 p-3 rounded-md bg-green-50 border border-green-200">
                            <div className="flex items-start gap-2">
                              <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 text-xs">
                                <div className="font-semibold text-green-900">
                                  {fdicLookup.institutionName}
                                  {fdicLookup.city && fdicLookup.state &&
                                    <span className="font-normal text-green-700"> · {fdicLookup.city}, {fdicLookup.state}</span>}
                                </div>
                                {fdicLookup.primaryRegulator && (
                                  <div className="text-green-800 mt-1">
                                    Primary regulator: <span className="font-semibold">{fdicLookup.primaryRegulator}</span>
                                    {fdicLookup.primaryRegulator !== primaryRegulator && primaryRegulator && (
                                      <span className="ml-2 text-amber-700 font-medium">
                                        ⚠ You selected {primaryRegulator}. Step 0 to revise.
                                      </span>
                                    )}
                                    {fdicLookup.primaryRegulator === primaryRegulator && (
                                      <span className="ml-1 text-green-700">— auto-applied to your profile</span>
                                    )}
                                  </div>
                                )}
                                {!fdicLookup.active && (
                                  <div className="text-amber-700 mt-1">
                                    ⚠ FDIC records show this institution as inactive.
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {fdicLookup && !fdicLookup.found && !fdicLookupLoading && (
                          <p className="text-xs text-amber-700 mt-2">
                            FDIC has no record of cert #{fdicCertNumber}. Double-check the number or verify the regulator manually.
                          </p>
                        )}

                        {fdicLookupError && !fdicLookupLoading && (
                          <p className="text-xs text-amber-700 mt-2">
                            {fdicLookupError}
                          </p>
                        )}
                      </div>

                      {/* LEI */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          LEI (Legal Entity Identifier)
                        </label>
                        <input
                          type="text"
                          value={institutionLei}
                          onChange={(e) => setInstitutionLei(e.target.value)}
                          placeholder="20-character LEI"
                          maxLength={20}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500"
                        />
                      </div>

                      {/* MSA Codes */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          MSA/MD Codes
                        </label>
                        <input
                          type="text"
                          value={msaCodes}
                          onChange={(e) => setMsaCodes(e.target.value)}
                          placeholder="e.g. 19740, 16980"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">Comma-separated MSA/MD codes for your assessment areas</p>
                      </div>

                      {/* Last CRA Exam Date — Wallace, May 2026 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last CRA Exam Date
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="date"
                            value={lastExamDate}
                            onChange={(e) => setLastExamDate(e.target.value)}
                            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500"
                          />
                          <button
                            type="button"
                            onClick={handleAutoDetectExamDate}
                            disabled={examDateLookupLoading || !institutionName.trim()}
                            className="px-3 py-2 rounded-lg border border-ocean-300 bg-ocean-50 text-ocean-700 text-sm font-medium hover:bg-ocean-100 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                            title={!institutionName.trim() ? 'Enter the institution name first' : 'Use AI to suggest the date based on the bank name'}
                          >
                            {examDateLookupLoading ? 'Looking up…' : 'Auto-detect'}
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 flex items-center">
                          <InformationCircleIcon className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                          Drives the Loan-to-Deposit ratio window. The LTD calc averages every quarter from the next quarter-end after this date through the most recent available.
                        </p>

                        {/* Suggestion display */}
                        {examDateSuggestion && (
                          <div className={`mt-2 p-3 rounded-md border ${
                            examDateSuggestion.examDate
                              ? examDateSuggestion.confidence >= 0.8
                                ? 'bg-green-50 border-green-200'
                                : 'bg-amber-50 border-amber-200'
                              : 'bg-gray-50 border-gray-200'
                          }`}>
                            <div className="flex items-start gap-2">
                              <div className="flex-1 text-xs">
                                {examDateSuggestion.examDate ? (
                                  <>
                                    <div className="font-semibold text-gray-900">
                                      Suggested: {examDateSuggestion.examDate}
                                      <span className="ml-2 text-gray-500 font-normal">
                                        ({(examDateSuggestion.confidence * 100).toFixed(0)}% confidence · {examDateSuggestion.source})
                                      </span>
                                    </div>
                                    <p className="text-gray-700 mt-1">{examDateSuggestion.reasoning}</p>
                                    {examDateSuggestion.examDate !== lastExamDate && (
                                      <button
                                        type="button"
                                        onClick={handleApplyExamDateSuggestion}
                                        className="mt-2 text-ocean-700 font-medium hover:text-ocean-900 underline-offset-2 hover:underline"
                                      >
                                        Apply this date →
                                      </button>
                                    )}
                                    {examDateSuggestion.examDate === lastExamDate && (
                                      <span className="mt-2 inline-block text-green-700 font-medium">
                                        ✓ Applied to the field above
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <div className="font-semibold text-gray-900">No confident match.</div>
                                    <p className="text-gray-700 mt-1">{examDateSuggestion.reasoning}</p>
                                    <p className="text-gray-500 mt-1">Please enter the date manually using the field above.</p>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        {examDateLookupError && !examDateLookupLoading && (
                          <p className="text-xs text-amber-700 mt-2">{examDateLookupError}</p>
                        )}
                      </div>

                      {/* CRA Asset Size */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          CRA Asset Size
                        </label>
                        <p className="text-xs text-gray-500 mb-2">
                          Enter your CRA asset size based on total assets at 12/31 for the two prior year-ends.
                        </p>
                        <select
                          value={assetSizeBucket}
                          onChange={(e) => setAssetSizeBucket(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500"
                        >
                          <option value="">Select size range</option>
                          {ASSET_BUCKETS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                        </select>
                      </div>

                      {/* Peer Asset Range — hidden 2026-04-26 pending Michael's reworked peer methodology.
                          State + mutation are preserved so the field comes back without a schema change.
                          To restore: re-add the JSX block (see git history at commit 1797c0e^). */}
                    </div>

                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={() => setStep(0)}
                        className="px-4 py-3 rounded-lg font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center"
                      >
                        <ArrowLeftIcon className="w-4 h-4 mr-1" />
                        Back
                      </button>
                      <button
                        onClick={handleSaveAndContinue}
                        disabled={saveMutation.isPending}
                        className="flex-1 bg-ocean-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-ocean-700 focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {saveMutation.isPending ? 'Saving...' : 'Save & Continue'}
                      </button>
                      <button
                        onClick={() => {
                          // Skip optional fields — still save step 0 data
                          handleSaveAndContinue();
                        }}
                        disabled={saveMutation.isPending}
                        className="px-4 py-3 rounded-lg font-semibold text-gray-500 hover:text-gray-700 transition-colors text-sm"
                      >
                        Skip for now
                      </button>
                    </div>
                  </>
                )}

                {/* Step 2: Branches (skippable) */}
                {step === 2 && (
                  <>
                    <Dialog.Title as="h3" className="text-2xl font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <MapPinIcon className="w-6 h-6 text-ocean-600" />
                      Branches
                    </Dialog.Title>
                    <Dialog.Description className="text-gray-600 mb-4">
                      Branch and LPO data drives R12 redlining analysis and CRA assessment-area validation. Import now or come back later from the Branches tab in the sidebar.
                    </Dialog.Description>

                    <div className="max-h-[420px] overflow-y-auto pr-1 -mx-1 px-1">
                      <BranchesPanel />
                    </div>

                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={() => setStep(1)}
                        className="px-4 py-3 rounded-lg font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center"
                      >
                        <ArrowLeftIcon className="w-4 h-4 mr-1" />
                        Back
                      </button>
                      <button
                        onClick={() => setStep(3)}
                        className="flex-1 bg-ocean-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-ocean-700 focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:ring-offset-2 transition-colors"
                      >
                        Continue
                      </button>
                      <button
                        onClick={() => setStep(3)}
                        className="px-4 py-3 rounded-lg font-semibold text-gray-500 hover:text-gray-700 transition-colors text-sm"
                      >
                        Skip for now
                      </button>
                    </div>
                  </>
                )}

                {/* Step 3: All Set */}
                {step === 3 && (
                  <>
                    <div className="text-center mb-6">
                      <div className="mx-auto w-16 h-16 rounded-full bg-mint-100 flex items-center justify-center mb-4">
                        <CheckCircleIcon className="w-8 h-8 text-mint-600" />
                      </div>
                      <Dialog.Title as="h3" className="text-2xl font-bold text-navy-900 mb-2">
                        Institution configured!
                      </Dialog.Title>
                      <Dialog.Description className="text-gray-600">
                        Your setup is complete. Modules like Peer Benchmarking and Financial Metrics are now ready to use.
                      </Dialog.Description>
                    </div>

                    {/* Summary */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Institution</span>
                        <span className="font-medium text-gray-900">{institutionName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Regulator</span>
                        <span className="font-medium text-gray-900">{primaryRegulator}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Charter</span>
                        <span className="font-medium text-gray-900">{charterType.replace(/_/g, ' ')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">State</span>
                        <span className="font-medium text-gray-900">{state}</span>
                      </div>
                      {fdicCertNumber && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">FDIC Cert</span>
                          <span className="font-medium text-gray-900">{fdicCertNumber}</span>
                        </div>
                      )}
                      {assetSizeBucket && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">CRA Asset Size</span>
                          <span className="font-medium text-gray-900">{ASSET_BUCKETS.find(b => b.value === assetSizeBucket)?.label || assetSizeBucket}</span>
                        </div>
                      )}
                    </div>

                    {/* Quick links */}
                    <div className="space-y-2">
                      <button
                        onClick={() => handleFinish('/peer-benchmarking')}
                        className="w-full flex items-center p-3 rounded-lg border-2 border-ocean-200 bg-ocean-50 hover:border-ocean-400 transition-colors text-left"
                      >
                        <div className="flex-1">
                          <h4 className="font-semibold text-navy-900 text-sm">Peer Benchmarking</h4>
                          <p className="text-xs text-gray-600">Compare performance against HMDA and FDIC peer data</p>
                        </div>
                        <ArrowRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      </button>
                      <button
                        onClick={() => handleFinish('/upload')}
                        className="w-full flex items-center p-3 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-colors text-left"
                      >
                        <div className="flex-1">
                          <h4 className="font-semibold text-navy-900 text-sm">Upload Marketing</h4>
                          <p className="text-xs text-gray-600">Scan an ad for compliance with AI-powered checks</p>
                        </div>
                        <ArrowRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      </button>
                      <button
                        onClick={() => handleFinish('/dashboard')}
                        className="w-full flex items-center p-3 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-colors text-left"
                      >
                        <div className="flex-1">
                          <h4 className="font-semibold text-navy-900 text-sm">Dashboard</h4>
                          <p className="text-xs text-gray-600">See your compliance health score</p>
                        </div>
                        <ArrowRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      </button>
                    </div>

                    <button
                      onClick={() => handleFinish()}
                      className="w-full mt-4 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      Close
                    </button>
                  </>
                )}

                <p className="text-xs text-gray-500 mt-4 text-center">
                  You can update this anytime from Settings
                </p>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
